import type { CaseView } from "@/api/types/CaseView";
import type { AppPlatform } from "@/api/types/AppPlatform";
import type { Role } from "@/api/types/Role";
import type { RoundCounts } from "@/api/types/RoundCounts";
import type { RoundSummary } from "@/api/types/RoundSummary";
import type { TestStage } from "@/api/types/TestStage";

/** What the caller's effective project role allows (mirrors docs/API.md). */
export function permissions(role: Role) {
  return {
    /** Create/edit tasks and cases, comment, upload attachments. */
    edit: role !== "viewer",
    /** Project settings: statuses, custom fields; delete anyone's task or comment. */
    manage: role === "manager",
    /** Mark ready, request retest, reply to failures. */
    dev: role === "manager" || role === "developer",
    /** Start/finish rounds, record results, findings. */
    test: role === "manager" || role === "tester",
  };
}

export type Perms = ReturnType<typeof permissions>;

export const APP_PLATFORMS: AppPlatform[] = ["android", "ios", "desktop", "web", "backend"];

/** False for the free text of a case from before apps. */
export function isAppPlatform(p: string): p is AppPlatform {
  return (APP_PLATFORMS as string[]).includes(p);
}

/** What a round tested: its version, build and environment, those it has. */
export function roundMeta(r: Pick<RoundSummary, "release" | "build" | "environment">): string {
  const version = r.release ? `${r.release.app_name} ${r.release.name}` : "";
  return [version, r.build, r.environment].filter(Boolean).join(" · ");
}

/** Cases whose results should be re-checked: never tested, not passed last time, or changed since. */
export function needsRetest(c: CaseView): boolean {
  if (c.archived) return false;
  const recorded = [...c.results].reverse().find((r) => r.status !== null);
  if (!recorded) return true;
  return recorded.status !== "pass" || recorded.stale;
}

/** Display groups: consecutive runs of cases sharing a section, in display order. */
export function groupBySection<T extends { section: string }>(items: T[]): { section: string; items: T[] }[] {
  const groups: { section: string; items: T[] }[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.section === it.section) last.items.push(it);
    else groups.push({ section: it.section, items: [it] });
  }
  return groups;
}

export function recorded(c: RoundCounts): number {
  return c.total - c.untested;
}

/** Splits a free-text tag field into clean tags. */
export function parseTags(s: string): string[] {
  return Array.from(new Set(s.split(/[,\s]+/).map((t) => t.trim().toLowerCase()).filter(Boolean)));
}

/** What the Done warning needs to know about a task. */
export type GuardTask = { key: string; case_count: number; latest_round: unknown; test_stage: TestStage };

/**
 * Moving this task to a done status leaves it "not verified": it has testing in scope (cases or a round) and
 * testing hasn't passed. Mirrors `summary::not_verified` on the server, less the request, which a move out of
 * the Test column withdraws.
 */
export function unverifiedIfDone(t: GuardTask): boolean {
  return (t.case_count > 0 || t.latest_round != null) && t.test_stage !== "passed";
}
